import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import {
  Truck, MapPin, CheckCircle2, XCircle, Clock,
  Loader2, RefreshCw, User, ChevronRight, Camera,
} from 'lucide-react'
import { api, formatMoney } from '@/lib/api'

// Corrige ícones default do Leaflet que não carregam via bundler
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
})

interface Parada {
  pedido_id: number; pedido_numero: number
  sequencia: number | null
  cliente_id: number | null; cliente: string
  cidade: string | null; uf: string | null
  valor_previsto: number
  status: 'pendente' | 'entregue' | 'devolvido'
  lat: number | null; lon: number | null
  coord_origem: 'entrega' | 'cidade' | null
  entrega: null | {
    status: string; confirmado_em: string | null
    valor_recebido: number; foto_url: string | null
    observacao: string | null; motivo_nao_entrega: string | null
    recebimentos: any[]
  }
}

interface CargaMapa {
  id: number; numero: number; nome: string; status: string
  data_saida: string | null
  motorista: { id: number; nome: string } | null
  veiculo: { id: number; placa: string; modelo?: string | null } | null
  rota: { id: number; nome: string } | null
  paradas: Parada[]
  progresso: { total: number; entregues: number; devolvidas: number; pendentes: number }
}

interface MapaResp {
  cargas: CargaMapa[]
  bbox: { sw: [number, number]; ne: [number, number] } | null
}

// Ícones SVG inline coloridos por status
function makeIcon(cor: string, seq: number | null) {
  const html = `
    <div style="background:${cor};color:white;border:2px solid white;border-radius:50%;
      width:32px;height:32px;display:flex;align-items:center;justify-content:center;
      font-weight:bold;font-size:12px;box-shadow:0 1px 4px rgba(0,0,0,.4);">
      ${seq ?? '?'}
    </div>`
  return L.divIcon({ html, className: '', iconSize: [32, 32], iconAnchor: [16, 16] })
}

const COR_STATUS: Record<string, string> = {
  entregue: '#059669',     // verde
  devolvido: '#dc2626',    // vermelho
  pendente: '#64748b',     // cinza
}

// Sub-componente: ajusta o viewport pro bbox global ou da carga selecionada
function FitBounds({ pontos }: { pontos: [number, number][] }) {
  const map = useMap()
  useEffect(() => {
    if (pontos.length === 0) return
    if (pontos.length === 1) {
      map.setView(pontos[0], 12)
    } else {
      const b = L.latLngBounds(pontos.map(p => L.latLng(p[0], p[1])))
      map.fitBounds(b, { padding: [40, 40] })
    }
  }, [JSON.stringify(pontos)]) // eslint-disable-line react-hooks/exhaustive-deps
  return null
}

export default function MapaCargasPage() {
  const [filtro, setFiltro] = useState<'todas' | 'em_rota' | 'em_acerto'>('todas')
  const [filtroMot, setFiltroMot] = useState<number | null>(null)
  const [cargaSel, setCargaSel] = useState<number | null>(null)

  const { data, isLoading, refetch, isFetching } = useQuery<MapaResp>({
    queryKey: ['mapa-vivo'],
    queryFn: async () => (await api.get('/vendas/cargas/mapa-vivo')).data,
    refetchInterval: 60_000, // auto-refresh 60s
  })

  const cargas = data?.cargas || []
  const motoristas = useMemo(() => {
    const m = new Map<number, string>()
    cargas.forEach(c => { if (c.motorista) m.set(c.motorista.id, c.motorista.nome) })
    return Array.from(m.entries()).map(([id, nome]) => ({ id, nome }))
  }, [cargas])

  const cargasFiltradas = useMemo(() => {
    return cargas.filter(c =>
      (filtro === 'todas' || c.status === filtro) &&
      (!filtroMot || c.motorista?.id === filtroMot)
    )
  }, [cargas, filtro, filtroMot])

  const cargaAtual = cargaSel ? cargas.find(c => c.id === cargaSel) : null

  // Pontos a exibir no mapa: se há carga selecionada, só dela; senão, todas filtradas
  const pontosMapa = useMemo(() => {
    const cs = cargaAtual ? [cargaAtual] : cargasFiltradas
    const pts: [number, number][] = []
    cs.forEach(c => c.paradas.forEach(p => {
      if (p.lat != null && p.lon != null) pts.push([p.lat, p.lon])
    }))
    return pts
  }, [cargaAtual, cargasFiltradas])

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Truck size={22} className="text-brand-600" /> Mapa de Cargas
          </h1>
          <p className="text-sm text-ink-500">
            Cargas na rua agora — paradas, motorista, status. Atualiza a cada 60s.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select className="input !py-1.5 text-sm" value={filtro}
            onChange={e => setFiltro(e.target.value as any)}>
            <option value="todas">Todas</option>
            <option value="em_rota">Em rota</option>
            <option value="em_acerto">Em acerto</option>
          </select>
          <select className="input !py-1.5 text-sm" value={filtroMot || ''}
            onChange={e => setFiltroMot(e.target.value ? Number(e.target.value) : null)}>
            <option value="">Todos motoristas</option>
            {motoristas.map(m => <option key={m.id} value={m.id}>{m.nome}</option>)}
          </select>
          <button onClick={() => refetch()} className="btn-ghost text-sm" title="Atualizar agora">
            {isFetching ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          </button>
        </div>
      </div>

      {/* Layout principal: 70% mapa, 30% painel */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr,360px] gap-3 min-h-0">
        {/* MAPA */}
        <div className="relative rounded-xl overflow-hidden border border-ink-200 shadow-sm bg-ink-100">
          {isLoading ? (
            <div className="absolute inset-0 flex items-center justify-center text-ink-500">
              <Loader2 size={24} className="animate-spin mr-2" /> Carregando mapa...
            </div>
          ) : pontosMapa.length === 0 ? (
            <div className="absolute inset-0 flex items-center justify-center text-ink-500 text-center p-6">
              <div>
                <MapPin size={36} className="mx-auto mb-3 text-ink-300" />
                <p className="font-medium">Sem cargas na rua no momento</p>
                <p className="text-xs mt-1">
                  Quando uma carga sair pra rota você vai ver as paradas aqui.
                </p>
              </div>
            </div>
          ) : (
            <MapContainer
              center={[-8.0, -39.5]}    // CE/PE Sertão como centro default
              zoom={7}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FitBounds pontos={pontosMapa} />
              {(cargaAtual ? [cargaAtual] : cargasFiltradas).map(c => {
                const paradasComCoord = c.paradas
                  .filter(p => p.lat != null && p.lon != null)
                  .sort((a, b) => (a.sequencia || 999) - (b.sequencia || 999))
                const linha = paradasComCoord.map(p => [p.lat!, p.lon!] as [number, number])
                return (
                  <div key={c.id}>
                    {/* Trajeto ligando as paradas na sequência */}
                    {linha.length > 1 && (
                      <Polyline
                        positions={linha}
                        pathOptions={{
                          color: c.status === 'em_acerto' ? '#0284c7' : '#7c3aed',
                          weight: 3, opacity: 0.6, dashArray: '6 8',
                        }}
                      />
                    )}
                    {paradasComCoord.map(p => (
                      <Marker
                        key={p.pedido_id}
                        position={[p.lat!, p.lon!]}
                        icon={makeIcon(COR_STATUS[p.status], p.sequencia)}
                      >
                        <Popup>
                          <div className="text-xs space-y-1 min-w-[200px]">
                            <div className="font-bold text-sm flex items-center gap-1">
                              {p.status === 'entregue' && <CheckCircle2 size={14} className="text-emerald-600" />}
                              {p.status === 'devolvido' && <XCircle size={14} className="text-rose-600" />}
                              {p.status === 'pendente' && <Clock size={14} className="text-ink-500" />}
                              Parada {p.sequencia ?? '?'} · {p.cliente}
                            </div>
                            <div className="text-ink-500">
                              {[p.cidade, p.uf].filter(Boolean).join('/')}
                            </div>
                            <div>
                              Pedido <strong>#{p.pedido_numero}</strong> · {formatMoney(p.valor_previsto)}
                            </div>
                            {p.entrega && (
                              <div className="border-t pt-1 mt-1 space-y-1">
                                {p.entrega.confirmado_em && (
                                  <div className="text-ink-500">
                                    {new Date(p.entrega.confirmado_em).toLocaleString('pt-BR')}
                                  </div>
                                )}
                                {p.entrega.valor_recebido > 0 && (
                                  <div>Recebido: <strong>{formatMoney(p.entrega.valor_recebido)}</strong></div>
                                )}
                                {p.entrega.motivo_nao_entrega && (
                                  <div className="text-rose-600">↳ {p.entrega.motivo_nao_entrega}</div>
                                )}
                                {p.entrega.foto_url && (
                                  <a href={p.entrega.foto_url} target="_blank" rel="noreferrer"
                                    className="text-brand-600 inline-flex items-center gap-1">
                                    <Camera size={11} /> Ver foto
                                  </a>
                                )}
                              </div>
                            )}
                            {p.coord_origem === 'cidade' && p.status === 'pendente' && (
                              <div className="text-amber-700 italic">
                                * coord da cidade (motorista ainda não confirmou)
                              </div>
                            )}
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                  </div>
                )
              })}
            </MapContainer>
          )}
        </div>

        {/* PAINEL LATERAL — lista de cargas */}
        <div className="rounded-xl border border-ink-200 shadow-sm bg-white overflow-y-auto">
          {cargaAtual ? (
            <div>
              <button onClick={() => setCargaSel(null)}
                className="w-full text-left px-4 py-3 border-b text-xs text-ink-500 hover:bg-ink-50">
                ← Voltar pra lista ({cargasFiltradas.length} cargas)
              </button>
              <CargaDetalhe carga={cargaAtual} />
            </div>
          ) : (
            <div>
              <div className="px-4 py-3 border-b flex items-center justify-between">
                <h3 className="font-semibold text-sm">Cargas na rua</h3>
                <span className="text-xs text-ink-500">{cargasFiltradas.length}</span>
              </div>
              {cargasFiltradas.length === 0 ? (
                <div className="text-center text-ink-500 py-8 text-sm">
                  Nenhuma carga em rota.
                </div>
              ) : (
                <div className="divide-y">
                  {cargasFiltradas.map(c => (
                    <CargaItem key={c.id} carga={c} onClick={() => setCargaSel(c.id)} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function CargaItem({ carga, onClick }: { carga: CargaMapa; onClick: () => void }) {
  const pct = carga.progresso.total > 0
    ? Math.round((carga.progresso.entregues + carga.progresso.devolvidas) / carga.progresso.total * 100)
    : 0
  return (
    <button onClick={onClick} className="w-full text-left px-4 py-3 hover:bg-brand-50 transition">
      <div className="flex items-center justify-between mb-1">
        <div className="font-medium text-sm truncate">
          #{carga.numero} · {carga.nome}
        </div>
        <ChevronRight size={14} className="text-ink-400 shrink-0" />
      </div>
      <div className="text-xs text-ink-500 flex items-center gap-2 mb-2">
        {carga.motorista && (
          <span className="flex items-center gap-1">
            <User size={11} /> {carga.motorista.nome.split(' ')[0]}
          </span>
        )}
        {carga.veiculo && <span>· {carga.veiculo.placa}</span>}
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
          carga.status === 'em_rota' ? 'bg-violet-100 text-violet-700' : 'bg-sky-100 text-sky-700'
        }`}>
          {carga.status === 'em_rota' ? 'Em rota' : 'Em acerto'}
        </span>
      </div>
      <div className="flex items-center gap-2 text-xs">
        <div className="flex-1 bg-ink-100 rounded-full h-1.5 overflow-hidden">
          <div className="bg-emerald-500 h-full" style={{ width: `${pct}%` }} />
        </div>
        <span className="text-ink-500 tabular-nums">
          {carga.progresso.entregues + carga.progresso.devolvidas}/{carga.progresso.total}
        </span>
      </div>
    </button>
  )
}

function CargaDetalhe({ carga }: { carga: CargaMapa }) {
  return (
    <div className="p-4 space-y-3">
      <div>
        <h2 className="font-semibold text-base">#{carga.numero} · {carga.nome}</h2>
        <div className="text-xs text-ink-500">
          {carga.status === 'em_rota' ? '🚛 Em rota' : '📥 Em acerto'}
          {carga.data_saida && ` · saiu em ${new Date(carga.data_saida).toLocaleString('pt-BR')}`}
        </div>
      </div>

      <div className="bg-ink-50 rounded-lg p-3 text-xs space-y-1">
        {carga.motorista && (
          <div className="flex items-center gap-1">
            <User size={12} className="text-ink-400" /> Motorista: <strong>{carga.motorista.nome}</strong>
          </div>
        )}
        {carga.veiculo && (
          <div>
            <Truck size={12} className="inline mr-1 text-ink-400" />
            Veículo: <strong>{carga.veiculo.placa}</strong>
            {carga.veiculo.modelo && ` (${carga.veiculo.modelo})`}
          </div>
        )}
        {carga.rota && <div>Rota: <strong>{carga.rota.nome}</strong></div>}
      </div>

      {/* Progresso */}
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
          <div className="text-xs text-emerald-700">Entregues</div>
          <div className="text-lg font-bold text-emerald-700">{carga.progresso.entregues}</div>
        </div>
        <div className="bg-rose-50 border border-rose-200 rounded p-2">
          <div className="text-xs text-rose-700">Devolvidas</div>
          <div className="text-lg font-bold text-rose-700">{carga.progresso.devolvidas}</div>
        </div>
        <div className="bg-ink-50 border border-ink-200 rounded p-2">
          <div className="text-xs text-ink-600">Pendentes</div>
          <div className="text-lg font-bold text-ink-600">{carga.progresso.pendentes}</div>
        </div>
      </div>

      {/* Lista de paradas */}
      <div>
        <h3 className="text-xs font-semibold text-ink-500 uppercase mb-2">Paradas</h3>
        <div className="space-y-1.5 max-h-[40vh] overflow-y-auto">
          {carga.paradas.map(p => (
            <div key={p.pedido_id}
              className={`border rounded p-2 text-xs ${
                p.status === 'entregue' ? 'border-emerald-200 bg-emerald-50/40' :
                p.status === 'devolvido' ? 'border-rose-200 bg-rose-50/40' :
                'border-ink-200 bg-white'
              }`}>
              <div className="flex items-center justify-between mb-0.5">
                <div className="font-medium flex items-center gap-1.5">
                  {p.status === 'entregue' && <CheckCircle2 size={12} className="text-emerald-600" />}
                  {p.status === 'devolvido' && <XCircle size={12} className="text-rose-600" />}
                  {p.status === 'pendente' && <Clock size={12} className="text-ink-400" />}
                  {p.sequencia ?? '?'}. {p.cliente}
                </div>
                <span className="font-mono text-[10px] text-ink-500">#{p.pedido_numero}</span>
              </div>
              <div className="text-ink-500 ml-5">
                {[p.cidade, p.uf].filter(Boolean).join('/')} · {formatMoney(p.valor_previsto)}
              </div>
              {p.entrega?.confirmado_em && (
                <div className="text-[10px] text-ink-400 ml-5">
                  ↳ {new Date(p.entrega.confirmado_em).toLocaleString('pt-BR')}
                  {p.entrega.foto_url && (
                    <a href={p.entrega.foto_url} target="_blank" rel="noreferrer"
                      className="text-brand-600 ml-2 inline-flex items-center gap-0.5">
                      <Camera size={10} /> foto
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
